import { useEffect, useMemo, useState } from "react";
import { niches, type NicheSummary } from "../../api/client";
import { useDuviCoreAdminQuery } from "../../hooks/use-api";

export const DUVI_CORE_ADMIN_NICHE_ENVS = [
  "dev",
  "stg",
  "prd",
  "shared",
] as const;

const PAGE_SIZE = 50;

export function duviCoreAdminFormatNicheDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function useDuviCoreAdminNichesList() {
  const [search, setSearch] = useState("");
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [cursorStack, setCursorStack] = useState<Array<string | undefined>>([]);

  const { data, meta, isLoading, error } = useDuviCoreAdminQuery(
    () => niches.list({ cursor, limit: PAGE_SIZE }),
    [cursor],
  );

  const allNiches: NicheSummary[] = data ?? [];

  const filteredNiches = useMemo(() => {
    if (!search.trim()) return allNiches;
    const q = search.trim().toLowerCase();
    return allNiches.filter(
      (n) =>
        n.name.toLowerCase().includes(q) ||
        n.short_code.toLowerCase().includes(q) ||
        n.domain.toLowerCase().includes(q),
    );
  }, [allNiches, search]);

  const nextCursor = meta?.cursor ?? null;
  const hasNext = Boolean(nextCursor);
  const hasPrev = cursorStack.length > 0;

  useEffect(() => {
    // reset cursor state when a search narrows the current page set
  }, [search]);

  const handleNext = () => {
    if (!hasNext || !nextCursor) return;
    setCursorStack((s) => [...s, cursor]);
    setCursor(nextCursor);
  };

  const handlePrev = () => {
    if (!hasPrev) return;
    setCursorStack((s) => {
      const next = s.slice(0, -1);
      const previousCursor = s[s.length - 1];
      setCursor(previousCursor);
      return next;
    });
  };

  return {
    search,
    setSearch,
    niches: filteredNiches,
    isLoading,
    error,
    hasNext,
    hasPrev,
    handleNext,
    handlePrev,
  };
}
